import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { GQL_CLIENT } from 'src/graphqlClient'
import { AccountType, ProjectFileUsage, ProjectStep } from 'src/utils/enums'
import {
  ProjectFileUsageMeta,
  fileUsageRequiredCount,
  isFileUsageSystemGenerated,
} from 'src/utils/projectFiles'
import { isProjectHubRole } from 'src/utils/projectHubRole'
import {
  isUsageOptedOutForProjectType,
  resolveDocumentAccess,
} from '@autoriz/shared/files'
import {
  getDocumentsView,
  type DocumentsViewPayload,
} from '../services/documents.gql'
import type {
  FileView,
  UsageGroupView,
  UsageView,
  MatrixGroupView,
} from '../types/documents.types'
import { computeUsageStatus } from '../utils/computeUsageStatus'
import { sortUsages } from '../utils/sortUsages'
import { sectionForUsage } from '../utils/sectionForUsage'
import { MATRIX_GROUPS, matrixGroupForUsage } from '../utils/matrixGroups'

export type UseDocumentsViewResult = {
  groups: UsageGroupView[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<unknown>
}

export const DOCUMENTS_VIEW_QUERY_KEY = (projectID: number) =>
  ['documents-view', projectID] as const

type VisibilityContext = {
  accountType?: AccountType
  projectStep?: ProjectStep
}

/**
 * The status + context half of a rule lookup for one usage.
 *
 * Passing the usage's own files is what turns the shared rules from a purely
 * structural answer into the real one — it is how "a slot the commercial can no
 * longer fill only shows once it holds a document" is enforced. The three gates
 * that used to be hard-coded here (the UA requestor set, the project-type
 * opt-out and the town-planning `hasUrbanAnalysis` check) are now declarative
 * rules in `@autoriz/shared/files`.
 */
type UsageContext = {
  files: ReadonlyArray<{ status: 'ACCEPTED' | 'REJECTED' | null }>
  hasUrbanAnalysis: boolean
  urbanAnalysisRequestor: AccountType | null
  projectType: string | null
}

/**
 * Role and step are passed through even when absent: the rule model treats a
 * missing role/step as "any", so the role-agnostic rules — notably "the
 * town-planning section is irrelevant until a study is delivered" — stay in
 * force for callers with no viewer context (fixtures, and the shared cache
 * consumers that only need the group shapes).
 */
const isUsageVisibleForContext = (
  usage: ProjectFileUsage,
  ctx: VisibilityContext,
  usageCtx: UsageContext
): boolean =>
  resolveDocumentAccess(usage, {
    accountType: ctx.accountType ?? null,
    step: ctx.projectStep ?? null,
    files: usageCtx.files,
    hasUrbanAnalysis: usageCtx.hasUrbanAnalysis,
    urbanAnalysisRequestor: usageCtx.urbanAnalysisRequestor,
    projectType: usageCtx.projectType,
  }).visible

export const buildGroupsFromPayload = (
  payload: DocumentsViewPayload,
  ctx: VisibilityContext = {}
): UsageGroupView[] => {
  // Context shared by every usage's rule lookup. The town-planning gate ("no
  // study delivered → the whole section is irrelevant"), the UA-requestor gate
  // and the project-type opt-out are all rules in @autoriz/shared/files now;
  // this only has to supply the facts they read.
  const hasUrbanAnalysis = payload.urban_analysis_files.length > 0
  const uaRequestor = payload.urban_analysis?.[0]?.requestor ?? null
  const projectType = payload.project?.project_type ?? null
  // 1. Flatten both arrays into a single FileView[].
  const files: FileView[] = [
    ...payload.project_files.flatMap((pf): FileView[] =>
      pf.file
        ? [
            {
              id: pf.file.id,
              name: pf.file.name,
              mime_type: pf.file.mime_type,
              size_bytes: pf.file.size,
              modified_at: pf.file.updated_at,
              created_at: pf.file.created_at,
              status: pf.status,
              usage: pf.use,
              origin: 'project',
              comment: pf.comment ?? undefined,
            },
          ]
        : []
    ),
    ...payload.urban_analysis_files.flatMap((uaf): FileView[] =>
      uaf.file
        ? [
            {
              id: uaf.file.id,
              name: uaf.file.name,
              mime_type: uaf.file.mime_type,
              size_bytes: uaf.file.size,
              modified_at: uaf.file.updated_at,
              created_at: uaf.file.created_at,
              status: uaf.status,
              usage: uaf.use,
              origin: 'urban_analysis',
              comment: uaf.comment ?? undefined,
            },
          ]
        : []
    ),
  ]

  // 2. Index files by usage, honoring the configured source: usages declared
  //    as `source: 'urban_analysis'` only accept UA-origin files; all others
  //    only accept project-origin files. Anything arriving via the "wrong"
  //    origin is ignored here so the group tally reflects the canonical
  //    source for that usage.
  const filesByUsage = new Map<ProjectFileUsage, FileView[]>()
  for (const f of files) {
    const configuredSource = ProjectFileUsageMeta[f.usage]?.source ?? 'project'
    if (f.origin !== configuredSource) continue
    const bucket = filesByUsage.get(f.usage) ?? []
    bucket.push(f)
    filesByUsage.set(f.usage, bucket)
  }
  //    Oldest upload first. The query already orders both source arrays, but
  //    sorting here is what makes the order a property of the view rather than
  //    of the payload — it survives a caller passing unordered fixtures, and it
  //    is the only thing ordering a usage whose files came from both arrays.
  for (const bucket of filesByUsage.values()) {
    bucket.sort((a, b) => a.created_at.localeCompare(b.created_at))
  }

  // 3. Matrix groups first — each consumes its member usages so they are
  //    not double-counted as standalone usage groups.
  const groups: UsageGroupView[] = []
  const consumedUsages = new Set<ProjectFileUsage>()

  // Shared facts every rule lookup below reads, minus the per-usage files.
  const usageCtxFor = (usage: ProjectFileUsage): UsageContext => ({
    files: filesByUsage.get(usage) ?? [],
    hasUrbanAnalysis,
    urbanAnalysisRequestor: uaRequestor,
    projectType,
  })

  for (const matrix of MATRIX_GROUPS) {
    const matrixFiles: FileView[] = []
    // Compute required from member usages: only count members that are
    // user-uploadable (not system-generated), matching the standalone logic.
    let required = 0
    for (const member of matrix.usages) {
      if (!ProjectFileUsageMeta[member]) continue
      if (!isFileUsageSystemGenerated(member)) {
        required += fileUsageRequiredCount(member, projectType)
      }
    }
    for (const member of matrix.usages) {
      const memberFiles = filesByUsage.get(member) ?? []
      if (memberFiles.length > 0) {
        // Keep only the latest file per member (FR-024).
        const latest = [...memberFiles].sort((a, b) =>
          b.modified_at.localeCompare(a.modified_at)
        )[0]
        if (latest) matrixFiles.push(latest)
      }
      consumedUsages.add(member)
    }
    const { tally, status } = computeUsageStatus({
      required,
      files: matrixFiles,
    })
    // Each member is judged against its OWN files, so a matrix group surfaces as
    // soon as any member it may show is populated.
    const is_visible = matrix.usages.some((u) =>
      isUsageVisibleForContext(u, ctx, usageCtxFor(u))
    )
    const group: MatrixGroupView = {
      key: matrix.key,
      title: matrix.title,
      section: matrix.section,
      usages: [...matrix.usages],
      required,
      files: matrixFiles,
      tally,
      status,
      is_visible,
      is_editable: true,
    }
    groups.push(group)
  }

  // 4. Standalone usages (not part of any matrix).
  const allUsages = Object.keys(ProjectFileUsageMeta) as ProjectFileUsage[]
  for (const usage of allUsages) {
    // UNDEFINED ("Autres documents") is a real category. On the hub
    // (SALESMAN/SOCIETY) it has its own dedicated section component, so skip it
    // as a row here to avoid duplication; every other role sees it as a normal
    // group on the /documents page.
    if (
      usage === ProjectFileUsage.UNDEFINED &&
      isProjectHubRole(ctx.accountType)
    ) {
      continue
    }
    if (consumedUsages.has(usage)) continue
    if (matrixGroupForUsage(usage)) continue
    // A usage opted out for this project type (e.g. POOL views/survey) is not
    // part of the dossier. Skip it only when empty, so it doesn't surface as a
    // requested/missing row; if files already exist (e.g. a relevé uploaded
    // before the opt-out, or an optional 3D insertion), keep the row — with
    // `required` forced to 0 below — so those files stay visible and manageable.
    const isOptedOut = isUsageOptedOutForProjectType(usage, projectType)
    if (isOptedOut && (filesByUsage.get(usage) ?? []).length === 0) continue
    const meta = ProjectFileUsageMeta[usage]
    if (!meta) continue
    // Usages the application fills itself, or that are filled through another
    // page (CERFA parts, fiche de projet, étude urbaine, matrice, …), carry no
    // requirement here — the rule model states that as "no documents page may
    // upload to this slot".
    const isSystemGenerated = isFileUsageSystemGenerated(usage)
    const isUrbanAnalysisSourced = meta.source === 'urban_analysis'
    // An opted-out usage that survives here (because files exist) carries no
    // requirement, so the surviving files aren't flagged missing/incomplete.
    const required = isSystemGenerated
      ? 0
      : fileUsageRequiredCount(usage, projectType)
    const uFiles = filesByUsage.get(usage) ?? []
    const { tally, status } = computeUsageStatus({
      required,
      files: uFiles,
    })
    const group: UsageView = {
      key: usage,
      title: meta.title,
      section: sectionForUsage(usage),
      required,
      files: uFiles,
      tally,
      status,
      is_visible: isUsageVisibleForContext(usage, ctx, usageCtxFor(usage)),
      is_editable: !isSystemGenerated && !isUrbanAnalysisSourced,
    }
    groups.push(group)
  }

  return groups
}

export const useDocumentsView = (
  projectID: number,
  ctx: VisibilityContext = {}
): UseDocumentsViewResult => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: DOCUMENTS_VIEW_QUERY_KEY(projectID),
    queryFn: () => getDocumentsView(GQL_CLIENT, projectID),
    enabled: !!projectID,
  })

  const groups = useMemo<UsageGroupView[]>(() => {
    if (!data) return []
    return sortUsages(buildGroupsFromPayload(data, ctx))
  }, [data, ctx.accountType, ctx.projectStep])

  return {
    groups,
    isLoading,
    error: error
      ? error instanceof Error
        ? error.message
        : 'Failed to load documents'
      : null,
    refetch,
  }
}
